/**
 * Feature Flags — runtime gating system.
 *
 * Replaces the build-time VITE_DATA_SOURCE 3-mode toggle + per-feature
 * env-var checks with a runtime flag system. The team can:
 *   - Ship a feature to one customer first; flip it off if it breaks
 *   - Gate per-org, per-user, per-role, or globally
 *   - Schedule effective windows
 *
 * Schema lives in supabase/migrations/00291_feature_flags.sql.
 *
 * Resolution priority (descending): user > org > role > environment > default.
 *
 * Composes with `[[audit-chain]]` — every override write should post a
 * `feature_flag.override.applied` entry via the audit chain service.
 */

import { supabase } from '@/lib/supabase'

// ── Types ────────────────────────────────────────────────────────────

export type FlagValueType = 'boolean' | 'string' | 'number' | 'json'
export type ScopeKind = 'org' | 'user' | 'role' | 'environment'

export interface FeatureFlag {
  key: string
  valueType: FlagValueType
  defaultValue: unknown
  description: string | null
  owner: string | null
  category: string | null
  referenceUrl: string | null
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
}

export interface FeatureFlagOverride {
  id: string
  flagKey: string
  scopeKind: ScopeKind
  scopeValue: string
  value: unknown
  effectiveFrom: string
  effectiveTo: string | null
  reason: string | null
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
}

export interface ResolveContext {
  userId?: string | null
  orgId?: string | null
  role?: string | null
  environment?: string | null
}

// ── Error helper ─────────────────────────────────────────────────────

export function describeFeatureFlagError(err: unknown): string {
  if (err instanceof Error) return err.message
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown }
    const msg = typeof e.message === 'string' ? e.message : ''
    const details = typeof e.details === 'string' ? e.details : ''
    const hint = typeof e.hint === 'string' ? e.hint : ''
    const code = typeof e.code === 'string' ? e.code : ''
    const combined = [msg, details, hint].filter(Boolean).join(' — ')
    if (combined) return code ? `${combined} [${code}]` : combined
    try { return JSON.stringify(err) } catch { /* fall through */ }
  }
  return String(err)
}

// ── Row mappers ──────────────────────────────────────────────────────

function mapFlagRow(row: Record<string, unknown>): FeatureFlag {
  return {
    key: String(row.key),
    valueType: row.value_type as FlagValueType,
    defaultValue: row.default_value,
    description: (row.description as string | null) ?? null,
    owner: (row.owner as string | null) ?? null,
    category: (row.category as string | null) ?? null,
    referenceUrl: (row.reference_url as string | null) ?? null,
    createdAt: String(row.created_at),
    createdBy: (row.created_by as string | null) ?? null,
    updatedAt: String(row.updated_at),
    updatedBy: (row.updated_by as string | null) ?? null,
  }
}

function mapOverrideRow(row: Record<string, unknown>): FeatureFlagOverride {
  return {
    id: String(row.id),
    flagKey: String(row.flag_key),
    scopeKind: row.scope_kind as ScopeKind,
    scopeValue: String(row.scope_value),
    value: row.value,
    effectiveFrom: String(row.effective_from),
    effectiveTo: (row.effective_to as string | null) ?? null,
    reason: (row.reason as string | null) ?? null,
    createdAt: String(row.created_at),
    createdBy: (row.created_by as string | null) ?? null,
    updatedAt: String(row.updated_at),
    updatedBy: (row.updated_by as string | null) ?? null,
  }
}

// ── Pure helpers (testable) ──────────────────────────────────────────

/**
 * Given a flag + overrides + a resolution context, return the resolved
 * value. Priority (descending): user > org > role > environment > default.
 * Skips overrides that are outside their effective window (relative to
 * the `asOf` date — defaults to now).
 */
export function resolveFlagValue(args: {
  flag: FeatureFlag
  overrides: FeatureFlagOverride[]
  context: ResolveContext
  asOf?: Date
}): unknown {
  const { flag, overrides, context, asOf = new Date() } = args
  const asOfIso = asOf.toISOString()
  // Filter to overrides for this flag that are currently in their window
  const active = overrides.filter(
    (o) =>
      o.flagKey === flag.key &&
      o.effectiveFrom <= asOfIso &&
      (o.effectiveTo === null || o.effectiveTo > asOfIso),
  )
  // Priority order
  const priority: Array<{ kind: ScopeKind; value: string | null | undefined }> = [
    { kind: 'user', value: context.userId },
    { kind: 'org', value: context.orgId },
    { kind: 'role', value: context.role },
    { kind: 'environment', value: context.environment },
  ]
  for (const { kind, value } of priority) {
    if (!value) continue
    const match = active.find((o) => o.scopeKind === kind && o.scopeValue === value)
    if (match) return match.value
  }
  return flag.defaultValue
}

/**
 * Validate a value against a flag's declared type. Returns null on
 * success or a string describing the mismatch.
 */
export function validateFlagValue(valueType: FlagValueType, value: unknown): string | null {
  if (valueType === 'boolean') {
    return typeof value === 'boolean' ? null : `Expected boolean, got ${typeof value}`
  }
  if (valueType === 'string') {
    return typeof value === 'string' ? null : `Expected string, got ${typeof value}`
  }
  if (valueType === 'number') {
    return typeof value === 'number' && Number.isFinite(value)
      ? null
      : `Expected finite number, got ${typeof value}`
  }
  if (valueType === 'json') {
    // Any JSON-serializable value passes. null is allowed.
    try {
      JSON.stringify(value)
      return null
    } catch {
      return 'Value is not JSON-serializable'
    }
  }
  return `Unknown value type: ${valueType}`
}

// ── DB-backed API ────────────────────────────────────────────────────

/** List every flag in the catalog, ordered by category + key. */
export async function listFlags(): Promise<FeatureFlag[]> {
  const { data, error } = await supabase
    .from('feature_flags')
    .select('*')
    .order('category', { ascending: true, nullsFirst: false })
    .order('key', { ascending: true })
  if (error) throw error
  return (data ?? []).map((r) => mapFlagRow(r as Record<string, unknown>))
}

/** Fetch a single flag by key. */
export async function getFlag(key: string): Promise<FeatureFlag | null> {
  const { data, error } = await supabase
    .from('feature_flags')
    .select('*')
    .eq('key', key)
    .maybeSingle()
  if (error) throw error
  return data ? mapFlagRow(data as Record<string, unknown>) : null
}

/** List active overrides for a flag (effective_to IS NULL or in the future). */
export async function listActiveOverrides(flagKey: string): Promise<FeatureFlagOverride[]> {
  const { data, error } = await supabase
    .from('feature_flag_overrides')
    .select('*')
    .eq('flag_key', flagKey)
    .or('effective_to.is.null,effective_to.gt.' + new Date().toISOString())
    .order('scope_kind', { ascending: true })
    .order('scope_value', { ascending: true })
  if (error) throw error
  return (data ?? []).map((r) => mapOverrideRow(r as Record<string, unknown>))
}

/** List every active override across every flag (admin dashboard use). */
export async function listAllActiveOverrides(): Promise<FeatureFlagOverride[]> {
  const { data, error } = await supabase
    .from('feature_flag_overrides')
    .select('*')
    .or('effective_to.is.null,effective_to.gt.' + new Date().toISOString())
    .order('flag_key', { ascending: true })
  if (error) throw error
  return (data ?? []).map((r) => mapOverrideRow(r as Record<string, unknown>))
}

/**
 * Upsert a flag definition. Use when registering a flag from app code
 * (e.g. on startup or via an admin "register" button). Idempotent on `key`.
 */
export async function registerFlag(args: {
  key: string
  valueType: FlagValueType
  defaultValue: unknown
  description?: string
  owner?: string
  category?: string
  referenceUrl?: string
  createdBy?: string
}): Promise<FeatureFlag> {
  const typeError = validateFlagValue(args.valueType, args.defaultValue)
  if (typeError) throw new Error(`Default value invalid: ${typeError}`)
  const { data, error } = await supabase
    .from('feature_flags')
    .upsert(
      {
        key: args.key,
        value_type: args.valueType,
        default_value: args.defaultValue,
        description: args.description ?? null,
        owner: args.owner ?? null,
        category: args.category ?? null,
        reference_url: args.referenceUrl ?? null,
        created_by: args.createdBy ?? null,
        updated_by: args.createdBy ?? null,
      },
      { onConflict: 'key' },
    )
    .select('*')
    .single()
  if (error) throw error
  return mapFlagRow(data as Record<string, unknown>)
}

/**
 * Add or update an override. If an active override already exists for
 * the same (flag_key, scope_kind, scope_value), it gets superseded:
 * the old one is closed (effective_to = now) and a new row is written.
 * This keeps the full history readable.
 */
export async function setOverride(args: {
  flagKey: string
  scopeKind: ScopeKind
  scopeValue: string
  value: unknown
  reason?: string
  effectiveFrom?: Date
  effectiveTo?: Date | null
  setBy?: string
}): Promise<FeatureFlagOverride> {
  // Type-check against the flag's declared type
  const flag = await getFlag(args.flagKey)
  if (!flag) throw new Error(`Flag not found: ${args.flagKey}`)
  const typeError = validateFlagValue(flag.valueType, args.value)
  if (typeError) throw new Error(`Override value invalid: ${typeError}`)

  // Close any existing active override for the same scope
  const now = new Date().toISOString()
  const { error: closeError } = await supabase
    .from('feature_flag_overrides')
    .update({ effective_to: now, updated_by: args.setBy ?? null })
    .eq('flag_key', args.flagKey)
    .eq('scope_kind', args.scopeKind)
    .eq('scope_value', args.scopeValue)
    .is('effective_to', null)
  if (closeError) throw closeError

  // Insert the new one
  const { data, error } = await supabase
    .from('feature_flag_overrides')
    .insert({
      flag_key: args.flagKey,
      scope_kind: args.scopeKind,
      scope_value: args.scopeValue,
      value: args.value,
      effective_from: (args.effectiveFrom ?? new Date()).toISOString(),
      effective_to: args.effectiveTo ? args.effectiveTo.toISOString() : null,
      reason: args.reason ?? null,
      created_by: args.setBy ?? null,
      updated_by: args.setBy ?? null,
    })
    .select('*')
    .single()
  if (error) throw error
  return mapOverrideRow(data as Record<string, unknown>)
}

/**
 * Clear an active override by setting effective_to = now. Doesn't delete
 * the row — keeps history. Idempotent (no-op if already closed).
 */
export async function clearOverride(args: {
  overrideId: string
  clearedBy?: string
}): Promise<void> {
  const { error } = await supabase
    .from('feature_flag_overrides')
    .update({
      effective_to: new Date().toISOString(),
      updated_by: args.clearedBy ?? null,
    })
    .eq('id', args.overrideId)
    .is('effective_to', null)
  if (error) throw error
}

/**
 * Resolve a single flag against the database. Used by the useFlag hook.
 * Fetches the flag + its active overrides in one round-trip and applies
 * resolveFlagValue.
 */
export async function resolveFlag(args: {
  key: string
  context: ResolveContext
}): Promise<unknown> {
  const flag = await getFlag(args.key)
  if (!flag) {
    // Flag not registered — return undefined so callers can fall back.
    return undefined
  }
  const overrides = await listActiveOverrides(args.key)
  return resolveFlagValue({ flag, overrides, context: args.context })
}
